// Cada recusa de uma relação institucional, traduzida em CAUSA e CAMPO.
//
// A API responde `400 validation_error` com `message: "Dados invalidos"` — o
// mesmo texto para qualquer recusa — e põe o motivo real em
// `details.relacao`, como um objeto `{ codigo, ... }`. O `mensagemDeErro` do
// produto não alcança esse formato: ele lê `details` esperando strings, e um
// objeto simplesmente não aparece. O operador veria "Dados inválidos" e nada
// mais, num formulário de seis campos.
//
// A regra que ordena este arquivo é a de `trava-margem-erros.ts`: quem lê
// precisa saber O QUE FAZER A SEGUIR. Por isso cada recusa devolve, além da
// frase, o CAMPO a marcar — e, quando existe, a relação que impede, para a tela
// oferecer o caminho até ela.
//
// O `switch` é EXAUSTIVO de propósito (`never` no default). `ErroRelacao` mora
// em `@atlas/types`, então um código novo no domínio quebra o typecheck aqui, e
// não na cara de quem estava cadastrando.

import { ApiHttpError } from "@atlas/sdk";
import type { ErroRelacao } from "@atlas/types";

/** O campo do formulário que a recusa marca. `null` = a recusa é do conjunto. */
export type CampoDaRelacao =
  | "responsavelId"
  | "atendidaId"
  | "papel"
  | "competenciaInicio"
  | "competenciaFim"
  | null;

export interface RecusaDaRelacao {
  codigo: ErroRelacao["codigo"];
  /** O que impede, numa frase, com a próxima ação quando ela não é óbvia. */
  texto: string;
  campo: CampoDaRelacao;
  /**
   * A relação já gravada que impede. `null` quando o impedimento vem de uma
   * relação DERIVADA do vínculo antigo — ela ainda não está no banco, e não há
   * ficha para onde apontar.
   */
  relacaoId?: number | null;
}

/** O `{ codigo, ... }` que a API põe em `details.relacao`, ou `null`. */
export function lerErroRelacao(err: unknown): ErroRelacao | null {
  const detalhes = (err as { details?: unknown } | null)?.details;
  if (typeof detalhes !== "object" || detalhes === null) return null;
  const relacao = (detalhes as { relacao?: unknown }).relacao;
  if (typeof relacao !== "object" || relacao === null) return null;
  const codigo = (relacao as { codigo?: unknown }).codigo;
  return typeof codigo === "string" ? (relacao as ErroRelacao) : null;
}

export function recusaDaRelacao(e: ErroRelacao): RecusaDaRelacao {
  switch (e.codigo) {
    case "relacao_consigo_mesma":
      return {
        codigo: e.codigo,
        campo: "atendidaId",
        texto:
          "Sem delegação, a organização já exerce todos os papéis sobre si — responsável e atendida precisam ser diferentes.",
      };
    case "competencia_invalida":
      return {
        codigo: e.codigo,
        campo: e.campo,
        texto: "Competência inválida. Use AAAAMM — por exemplo, 202609 para setembro de 2026.",
      };
    case "fim_antes_do_inicio":
      return {
        codigo: e.codigo,
        campo: "competenciaFim",
        texto: "A competência final é anterior à inicial. Deixe em branco para uma relação sem fim previsto.",
      };
    case "papel_desconhecido":
      return { codigo: e.codigo, campo: "papel", texto: "Escolha um dos papéis da lista." };
    case "conflito_processa_folha":
      return {
        codigo: e.codigo,
        campo: "papel",
        relacaoId: e.relacaoId,
        // O papel que ENVIA o arquivo é exclusivo: duas responsáveis mandando o
        // movimento da mesma folha produziriam dois arquivos oficiais para o
        // mesmo mês, e o ente executaria o que chegasse por último.
        texto:
          "Outra organização já processa a folha desta atendida no mesmo período. Encerre a relação atual antes de começar a nova, ou informe uma competência inicial posterior ao fim dela.",
      };
    case "ciclo_no_mesmo_papel":
      return {
        codigo: e.codigo,
        campo: "papel",
        relacaoId: e.relacaoId,
        texto:
          "As duas exerceriam este mesmo papel uma pela outra, ao mesmo tempo. Em papéis diferentes o arranjo é legítimo — só o mesmo papel nos dois sentidos se contradiz.",
      };
    case "relacao_duplicada":
      return {
        codigo: e.codigo,
        campo: "papel",
        relacaoId: e.relacaoId,
        texto: "Esta relação já existe, com este papel, no período informado.",
      };
    default: {
      // Código novo em `@atlas/types` sem tradução aqui: o typecheck quebra
      // nesta linha, e não em produção.
      const nunca: never = e;
      return { codigo: (nunca as ErroRelacao).codigo, campo: null, texto: "Relação recusada." };
    }
  }
}

/** A recusa já traduzida, direto do erro da API. `null` se for de outra natureza. */
export function recusaDoErro(err: unknown): RecusaDaRelacao | null {
  const e = lerErroRelacao(err);
  return e ? recusaDaRelacao(e) : null;
}

/**
 * A confirmação que a API exige antes de encerrar quem processa a folha de uma
 * organização com competência aberta (`409 confirmacao_necessaria`).
 *
 * O par recusa/confirma existe porque o efeito é invisível no clique e só
 * aparece na virada do mês, quando ninguém mais liga uma coisa à outra. A tela
 * precisa MOSTRAR quais competências ficariam sem processador antes de reenviar
 * com `confirmar: true`.
 */
export interface ConfirmacaoDeEncerramento {
  mensagem: string;
  folhas: { competencia: string; status: string }[];
}

export function lerConfirmacaoNecessaria(err: unknown): ConfirmacaoDeEncerramento | null {
  if (!(err instanceof ApiHttpError) || err.code !== "confirmacao_necessaria") return null;
  const d = err.details as { folhas?: { competencia: string; status: string }[] } | null;
  return { mensagem: err.message, folhas: Array.isArray(d?.folhas) ? d.folhas : [] };
}
